/**
 * MLB game status — the shared sample "games today" slate plus the per-player
 * in-game status derived from it. Used by:
 *   - LeagueScoreboard (Live Matchup): the MLB rail + rich player status
 *     (opponent, score, inning, baserunners, outs, and batting-order "due up").
 *   - Roster page: opponent + game time (not started) / score & inning (live/final).
 *
 * The FantasyData API is disabled app-wide (FANTASY_DATA_ENABLED = false), so
 * this is deterministic sample data. Keeping it in one module means a player's
 * game state stays consistent everywhere it's shown.
 */

import { getCurrentDate } from '../utils/rosterManager';

export const isGameLive = (game) => game?.Status === 'InProgress';
export const isGameFinal = (game) => game?.Status === 'Final' || game?.Status === 'F/OT';
export const isGameScheduled = (game) => !isGameLive(game) && !isGameFinal(game);

export const inningOrdinal = (n) => {
  if (n == null || n === '') return '';
  const d = Number(n);
  const v = d % 100;
  const suffix = (v >= 11 && v <= 13) ? 'th' : (d % 10 === 1 ? 'st' : d % 10 === 2 ? 'nd' : d % 10 === 3 ? 'rd' : 'th');
  return `${d}${suffix}`;
};

export const formatGameTime = (dateTimeStr) => {
  if (!dateTimeStr) return 'TBD';
  const d = new Date(dateTimeStr);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
};

// Every MLB team, so the sample slate below can round out to a near-full day.
const ALL_MLB_TEAMS = ['NYY', 'BOS', 'TB', 'TOR', 'BAL', 'CLE', 'DET', 'KC', 'MIN', 'CWS', 'HOU', 'LAA', 'OAK', 'SEA', 'TEX', 'ATL', 'MIA', 'NYM', 'PHI', 'WSH', 'CHC', 'CIN', 'MIL', 'PIT', 'STL', 'ARI', 'COL', 'LAD', 'SD', 'SF'];

// Stable [0,1) hash of a string for deterministic sample values.
const seedFrac = (s) => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  const x = Math.sin(Math.abs(h) + 1) * 10000;
  return x - Math.floor(x);
};

export const buildFallbackMlbGames = (mode = 'mid-season') => {
  const today = getCurrentDate();
  const games = [
    {
      GameID: 1, AwayTeam: 'NYY', HomeTeam: 'BOS', AwayTeamRuns: 3, HomeTeamRuns: 2,
      Status: 'InProgress', Inning: 7, InningHalf: 'T', Outs: 1, DateTime: `${today}T19:00:00`,
      RunnerOn1B: true, RunnerOn2B: true, RunnerOn3B: false,
      Detail: {
        AtBat: {
          Pitcher: { name: 'B. Bello', hand: 'RHP' },
          Batter: { name: 'A. Judge', hand: 'R' },
          Balls: 2, Strikes: 2,
          // x/y are % offsets inside the strike-zone card (0-100 from top-left).
          Pitches: [
            { n: 1, result: 'strike', x: 45, y: 34, desc: 'FB 97 · Strike looking' },
            { n: 2, result: 'ball', x: 22, y: 74, desc: 'SL 86 · Ball, low' },
            { n: 3, result: 'foul', x: 58, y: 42, desc: 'FB 96 · Foul back' },
            { n: 4, result: 'ball', x: 82, y: 60, desc: 'CH 88 · Ball, away' },
          ],
        },
        PlayByPlay: [
          {
            inning: 7, half: 'T', label: 'Yankees batting · now', score: 'NYY 3 · BOS 2',
            plays: [
              { kind: 'now', icon: '●', bold: 'A. Judge', text: 'at bat', sub: '2-2, 1 out' },
              { kind: '', icon: '1B', bold: 'J. Soto', text: 'singles to right', sub: 'runners on 1st & 2nd' },
              { kind: '', icon: 'BB', bold: 'A. Volpe', text: 'walks' },
              { kind: 'out', icon: 'K', bold: 'A. Rizzo', text: 'strikes out swinging' },
            ],
          },
          {
            inning: 6, half: 'B', label: 'Red Sox batting', score: 'NYY 3 · BOS 2',
            summary: 'Bot 6th · 1 R · 2 H · 1 LOB',
            plays: [
              { kind: 'run', icon: 'R', bold: 'R. Devers', text: 'doubles to left, Duran scores', sub: '3–2 NYY', run: '+1' },
              { kind: '', icon: '1B', bold: 'J. Duran', text: 'singles to center' },
              { kind: 'out', icon: 'F8', bold: 'T. Casas', text: 'flies out to center' },
            ],
          },
          {
            inning: 6, half: 'T', label: 'Yankees batting', score: 'NYY 3 · BOS 1',
            summary: 'Top 6th · 2 R · 2 H · 0 LOB',
            plays: [
              { kind: 'hr', icon: 'HR', bold: 'G. Stanton', text: 'homers to deep left', sub: '2-run shot', run: '+2' },
              { kind: '', icon: '1B', bold: 'G. Torres', text: 'singles to left' },
              { kind: 'out', icon: 'G3', bold: 'D. LeMahieu', text: 'grounds out to first' },
            ],
          },
        ],
        LineScore: {
          CurrentInning: 7,
          Away: { runs: [1, 0, 0, 0, 0, 2, 0, null, null], R: 3, H: 7, E: 0 },
          Home: { runs: [0, 1, 0, 0, 0, 1, null, null, null], R: 2, H: 6, E: 1 },
        },
        Box: {
          away: {
            batting: [
              { name: 'D. LeMahieu', pos: '2B', ab: 4, r: 0, h: 1, rbi: 0, bb: 0, k: 1, avg: '.268', ops: '.702' },
              { name: 'A. Judge', pos: 'CF', ab: 3, r: 1, h: 1, rbi: 0, bb: 1, k: 1, avg: '.301', ops: '1.024' },
              { name: 'J. Soto', pos: 'RF', ab: 4, r: 1, h: 2, rbi: 0, bb: 0, k: 0, avg: '.322', ops: '.988' },
              { name: 'G. Stanton', pos: 'DH', ab: 4, r: 1, h: 2, rbi: 2, bb: 0, k: 1, avg: '.244', ops: '.801' },
              { name: 'A. Rizzo', pos: '1B', ab: 4, r: 0, h: 0, rbi: 0, bb: 0, k: 2, avg: '.251', ops: '.720' },
              { name: 'G. Torres', pos: 'SS', ab: 3, r: 0, h: 1, rbi: 1, bb: 1, k: 0, avg: '.273', ops: '.756' },
            ],
            battingTotals: { ab: 22, r: 3, h: 7, rbi: 3, bb: 2, k: 5 },
            notes: [
              { group: 'Batting', items: [
                { b: 'HR:', t: 'Stanton (18, 6th inning off Bello, 1 on).' },
                { b: '2B:', t: 'Soto (21, Bello).' },
                { b: 'Team RISP:', t: '2-for-6. Team LOB: 4.' },
              ] },
              { group: 'Baserunning', items: [
                { b: 'SB:', t: 'Volpe (19, 2nd base off Bello/Wong).' },
              ] },
            ],
            pitching: [
              { name: 'C. Rodón', note: '', ip: '5.2', h: 5, r: 2, er: 2, bb: 2, k: 6, hr: 0, era: '3.42' },
              { name: 'I. Hamilton', note: 'H, 4', ip: '0.1', h: 1, r: 0, er: 0, bb: 0, k: 1, hr: 0, era: '2.75' },
            ],
            pitchingTotals: { ip: '6.0', h: 6, r: 2, er: 2, bb: 2, k: 7, hr: 0 },
          },
          home: {
            batting: [
              { name: 'J. Duran', pos: 'CF', ab: 4, r: 1, h: 2, rbi: 0, bb: 0, k: 0, avg: '.288', ops: '.822' },
              { name: 'R. Devers', pos: '3B', ab: 4, r: 0, h: 2, rbi: 1, bb: 0, k: 1, avg: '.296', ops: '.902' },
              { name: 'T. Casas', pos: '1B', ab: 4, r: 0, h: 0, rbi: 0, bb: 0, k: 2, avg: '.252', ops: '.784' },
              { name: 'M. Yoshida', pos: 'DH', ab: 3, r: 1, h: 1, rbi: 1, bb: 1, k: 0, avg: '.285', ops: '.780' },
              { name: 'T. Story', pos: 'SS', ab: 3, r: 0, h: 1, rbi: 0, bb: 0, k: 1, avg: '.244', ops: '.688' },
              { name: 'C. Wong', pos: 'C', ab: 3, r: 0, h: 0, rbi: 0, bb: 0, k: 1, avg: '.261', ops: '.700' },
            ],
            battingTotals: { ab: 21, r: 2, h: 6, rbi: 2, bb: 1, k: 5 },
            notes: [
              { group: 'Batting', items: [
                { b: '2B:', t: 'Devers (24, Rodón).' },
                { b: 'Team RISP:', t: '1-for-5. Team LOB: 5.' },
              ] },
              { group: 'Fielding', items: [
                { b: 'E:', t: 'Story (7, throw).' },
              ] },
            ],
            pitching: [
              { name: 'B. Bello', note: '', ip: '6.1', h: 7, r: 3, er: 3, bb: 2, k: 5, hr: 1, era: '3.88' },
            ],
            pitchingTotals: { ip: '6.1', h: 7, r: 3, er: 3, bb: 2, k: 5, hr: 1 },
          },
        },
        GameInfo: [
          { b: 'Pitches-strikes:', t: 'Rodón 92-58; Bello 88-55.' },
          { b: 'Groundouts-flyouts:', t: 'Rodón 7-4; Bello 8-5.' },
          { b: 'Batters faced:', t: 'Rodón 23; Bello 27.' },
          { b: 'Umpires:', t: 'HP: D. Iassogna. 1B: J. Tumpane. 2B: A. Hamari. 3B: C. Blaser.' },
          { b: 'Weather:', t: '68°, Cloudy. Wind: 9 mph, In From CF.' },
          { b: 'First pitch:', t: '7:11 PM. Venue: Fenway Park.' },
        ],
      },
    },
    { GameID: 2, AwayTeam: 'LAD', HomeTeam: 'SF', AwayTeamRuns: 0, HomeTeamRuns: 0, Status: 'Scheduled', DateTime: `${today}T21:45:00` },
    {
      GameID: 3, AwayTeam: 'ATL', HomeTeam: 'PHI', AwayTeamRuns: 5, HomeTeamRuns: 4,
      Status: 'Final', DateTime: `${today}T13:05:00`,
      Detail: {
        AtBat: null, // game over - Live tab shows play-by-play only
        PlayByPlay: [
          {
            inning: 9, half: 'T', label: 'Braves batting', score: 'ATL 5 · PHI 4',
            summary: 'Final · ATL 5, PHI 4',
            plays: [
              { kind: 'run', icon: 'R', bold: 'M. Olson', text: 'doubles to right, Acuña scores', sub: '5–4 ATL', run: '+1' },
              { kind: '', icon: '1B', bold: 'R. Acuña Jr.', text: 'singles to center' },
              { kind: 'out', icon: 'K', bold: 'A. Riley', text: 'strikes out looking' },
            ],
          },
        ],
        LineScore: {
          CurrentInning: null,
          Away: { runs: [0, 2, 0, 0, 1, 0, 1, 0, 1], R: 5, H: 10, E: 0 },
          Home: { runs: [1, 0, 3, 0, 0, 0, 0, 0, 0], R: 4, H: 8, E: 2 },
        },
        Box: {
          away: {
            batting: [
              { name: 'R. Acuña Jr.', pos: 'RF', ab: 5, r: 2, h: 3, rbi: 0, bb: 0, k: 1, avg: '.331', ops: '1.012' },
              { name: 'M. Olson', pos: '1B', ab: 4, r: 1, h: 2, rbi: 2, bb: 1, k: 1, avg: '.279', ops: '.900' },
              { name: 'A. Riley', pos: '3B', ab: 5, r: 0, h: 1, rbi: 1, bb: 0, k: 2, avg: '.270', ops: '.822' },
              { name: 'O. Albies', pos: '2B', ab: 4, r: 1, h: 2, rbi: 1, bb: 0, k: 0, avg: '.262', ops: '.760' },
            ],
            battingTotals: { ab: 18, r: 5, h: 10, rbi: 5, bb: 1, k: 4 },
            notes: [
              { group: 'Batting', items: [
                { b: '2B:', t: 'Olson 2 (19, Wheeler, Alvarado); Albies (14, Wheeler).' },
                { b: 'Team RISP:', t: '4-for-9. Team LOB: 6.' },
              ] },
            ],
            pitching: [
              { name: 'S. Strider', note: 'W, 11-3', ip: '7.0', h: 6, r: 4, er: 4, bb: 1, k: 11, hr: 2, era: '3.20' },
              { name: 'R. Iglesias', note: 'SV, 22', ip: '1.0', h: 0, r: 0, er: 0, bb: 0, k: 2, hr: 0, era: '2.10' },
            ],
            pitchingTotals: { ip: '9.0', h: 8, r: 4, er: 4, bb: 1, k: 14, hr: 2 },
          },
          home: {
            batting: [
              { name: 'K. Schwarber', pos: 'DH', ab: 4, r: 1, h: 1, rbi: 2, bb: 1, k: 2, avg: '.221', ops: '.812' },
              { name: 'T. Turner', pos: 'SS', ab: 5, r: 1, h: 2, rbi: 0, bb: 0, k: 0, avg: '.291', ops: '.810' },
              { name: 'B. Harper', pos: '1B', ab: 4, r: 1, h: 2, rbi: 1, bb: 1, k: 1, avg: '.302', ops: '.940' },
              { name: 'N. Castellanos', pos: 'RF', ab: 4, r: 1, h: 1, rbi: 1, bb: 0, k: 1, avg: '.255', ops: '.741' },
            ],
            battingTotals: { ab: 17, r: 4, h: 8, rbi: 4, bb: 2, k: 4 },
            notes: [
              { group: 'Batting', items: [
                { b: 'HR:', t: 'Schwarber (28, 3rd inning off Strider, 1 on); Harper (16, 3rd inning off Strider).' },
              ] },
              { group: 'Fielding', items: [
                { b: 'E:', t: 'Turner (9, fielding); Bohm (11, throw).' },
              ] },
            ],
            pitching: [
              { name: 'Z. Wheeler', note: 'L, 9-5', ip: '6.2', h: 8, r: 4, er: 4, bb: 1, k: 8, hr: 0, era: '3.05' },
              { name: 'J. Alvarado', note: '', ip: '2.1', h: 2, r: 1, er: 1, bb: 0, k: 3, hr: 0, era: '2.88' },
            ],
            pitchingTotals: { ip: '9.0', h: 10, r: 5, er: 5, bb: 1, k: 11, hr: 0 },
          },
        },
        GameInfo: [
          { b: 'Pitches-strikes:', t: 'Strider 102-71; Wheeler 98-66.' },
          { b: 'Batters faced:', t: 'Strider 28; Wheeler 29.' },
          { b: 'Weather:', t: '81°, Sunny. Wind: 7 mph, Out To RF.' },
          { b: 'First pitch:', t: '1:07 PM. Venue: Citizens Bank Park.' },
        ],
      },
    },
    // Intentionally minimal detail - exercises the tabs' missing-data fallbacks.
    { GameID: 4, AwayTeam: 'HOU', HomeTeam: 'SEA', AwayTeamRuns: 1, HomeTeamRuns: 2, Status: 'InProgress', Inning: 4, InningHalf: 'B', Outs: 2, DateTime: `${today}T22:10:00`, RunnerOn1B: false, RunnerOn2B: true, RunnerOn3B: false },
  ];

  // Round out the day's slate deterministically so most rostered players map to a
  // game today. These extra games carry only the state fields (no play-by-play
  // Detail) — enough for the rail and the per-player status.
  const used = new Set(games.flatMap((g) => [g.AwayTeam, g.HomeTeam]));
  const rest = ALL_MLB_TEAMS.filter((t) => !used.has(t));
  let gid = 20;
  for (let i = 0; i + 1 < rest.length; i += 2) {
    const away = rest[i];
    const home = rest[i + 1];
    const f = seedFrac(`${away}${home}`);
    const status = f < 0.6 ? 'InProgress' : f < 0.8 ? 'Final' : 'Scheduled';
    if (status === 'Scheduled') {
      games.push({ GameID: gid++, AwayTeam: away, HomeTeam: home, AwayTeamRuns: 0, HomeTeamRuns: 0, Status: 'Scheduled', Inning: null, InningHalf: null, Outs: 0, DateTime: `${today}T${18 + (i % 5)}:05:00`, RunnerOn1B: false, RunnerOn2B: false, RunnerOn3B: false });
    } else {
      games.push({
        GameID: gid++, AwayTeam: away, HomeTeam: home,
        AwayTeamRuns: Math.floor(seedFrac(`${away}a`) * 7),
        HomeTeamRuns: Math.floor(seedFrac(`${home}h`) * 7),
        Status: status,
        Inning: status === 'Final' ? 9 : 1 + Math.floor(seedFrac(`${away}${home}i`) * 8),
        InningHalf: seedFrac(`${away}${home}x`) < 0.5 ? 'T' : 'B',
        Outs: Math.floor(seedFrac(`${away}${home}o`) * 3),
        DateTime: `${today}T${13 + (i % 8)}:05:00`,
        RunnerOn1B: seedFrac(`${away}${home}1`) < 0.42,
        RunnerOn2B: seedFrac(`${away}${home}2`) < 0.32,
        RunnerOn3B: seedFrac(`${away}${home}3`) < 0.2,
      });
    }
  }

  // Phase the whole slate to match the season-phase test-data mode.
  if (mode === 'pre-draft') {
    // Offseason / spring: nothing has started — every game is upcoming.
    return games.map((g) => ({
      ...g, Detail: undefined, Status: 'Scheduled', AwayTeamRuns: 0, HomeTeamRuns: 0,
      Inning: null, InningHalf: null, Outs: 0, RunnerOn1B: false, RunnerOn2B: false, RunnerOn3B: false,
    }));
  }
  if (mode === 'after-season') {
    // Season over: every game has finished.
    return games.map((g) => (isGameFinal(g) ? g : {
      ...g, Status: 'Final', Inning: 9, InningHalf: 'T', Outs: 0,
      RunnerOn1B: false, RunnerOn2B: false, RunnerOn3B: false,
    }));
  }
  return games; // mid-season: today's live slate
};

// Deterministic batting-order spot (1-9) — the sample data has no real lineup,
// so derive a stable spot from the player id.
const battingSpot = (player) => 1 + (Math.floor(seedFrac(String(player?.id ?? player?.name ?? '')) * 1000) % 9);

/**
 * Link a rostered player to today's MLB game (by team code) and derive their
 * current situation. Returns null when the player has no game today. Shape:
 *   { kind: 'pre'|'live'|'final', opponent, score?, timeText?, finalText?,
 *     inningText?, outs?, game?, teamBatting?, due?, dueClass? }
 */
export const getPlayerGameStatus = (player, mlbGames) => {
  if (!player?.team || !Array.isArray(mlbGames)) return null;
  const game = mlbGames.find((g) => g.AwayTeam === player.team || g.HomeTeam === player.team);
  if (!game) return null;

  const isHome = game.HomeTeam === player.team;
  const opponent = `${isHome ? 'vs' : '@'} ${isHome ? game.AwayTeam : game.HomeTeam}`;
  const teamRuns = isHome ? game.HomeTeamRuns : game.AwayTeamRuns;
  const oppRuns = isHome ? game.AwayTeamRuns : game.HomeTeamRuns;
  const score = `${teamRuns ?? 0}-${oppRuns ?? 0}`;

  if (isGameFinal(game)) {
    const res = teamRuns > oppRuns ? 'W' : teamRuns < oppRuns ? 'L' : 'T';
    return { kind: 'final', opponent, score, finalText: `Final · ${res}`, win: teamRuns > oppRuns };
  }
  if (isGameScheduled(game)) {
    return { kind: 'pre', opponent, timeText: game.Status === 'Delayed' ? 'Delayed' : formatGameTime(game.DateTime) };
  }

  // Live
  const isPitcher = (player.positions || [player.position]).some((p) => p === 'P' || p === 'SP' || p === 'RP');
  const teamBatting = game.InningHalf === 'T' ? !isHome : isHome;
  const inningText = `${game.InningHalf === 'T' ? '▲' : '▼'} ${inningOrdinal(game.Inning)}`;

  let due = null;
  let dueClass = 'field';
  if (isPitcher) {
    due = teamBatting ? 'Dugout' : 'On mound';
    dueClass = teamBatting ? 'field' : 'mound';
  } else if (teamBatting) {
    const cur = 1 + (Math.floor(seedFrac(`${game.GameID}|${game.Inning}|${game.InningHalf}`) * 1000) % 9);
    const until = (battingSpot(player) - cur + 9) % 9;
    if (until === 0) { due = 'At bat'; dueClass = 'atbat'; }
    else if (until === 1) { due = 'On deck'; dueClass = 'ondeck'; }
    else if (until === 2) { due = 'In the hole'; dueClass = 'hole'; }
    else { due = `Due up ${until}`; dueClass = 'due'; }
  } else {
    due = 'In field';
    dueClass = 'field';
  }

  return { kind: 'live', opponent, score, inningText, outs: game.Outs, game, teamBatting, due, dueClass };
};
